<template>
  <div class="page-section">
    <div :class="containerClass">
      <div class="category-header d-flex flex-column align-items-start">
        <h2 class="text-left category-title">{{ categoryTitle }}</h2>
        <p class="text-left category-desc">{{ categoryDescription }}</p>
      </div>

      <div v-if="isPaymentStatusLoaded" class="row mb-5">
        <DashboardCard
          v-for="item in filteredItems"
          :key="item.id || item.abbr"
          :guide="item"
          :setupStatuses="setupStatuses"
          class="col-md-6 mb-2"
          @action-click="handleActionClick"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import Page from '@/components/Page.vue';
import DashboardCard from '@/components/DashboardCard.vue';
import { DASHBOARD_TYPES } from '@/common/constants';
import {
  ACADEMIC_EXCELLENCE_OPTIONS,
  SKILLS_DEVELOPMENT_OPTIONS,
  CAREER_OPPORTUNITIES_OPTIONS,
  CAREER_PREPARATION_OPTIONS,
  EDUCATION_OPTIONS,
  getCategoryItems,
  categorizeDashboardItems,
  mergeWithStaticConfig,
} from '@/common/dashboardCategories';

export default {
  extends: Page,
  name: 'CategoryDetail',
  components: { DashboardCard },
  props: {
    categoryType: {
      type: String,
      required: true,
      validator: (value) =>
        ['academic', 'skills_development', 'career_opportunities', 'career_preparation', 'education'].includes(value),
    },
  },
  data() {
    return {
      title: 'Category Detail',
      setupStatuses: {},
      isDataLoaded: false,
      isPaymentStatusLoaded: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['getLoggedInUser', 'getLoggedInUserDashboard']),
    ...mapGetters('school', ['getPaymentStatus', 'getInAppPaymentStatus']),

    categoryConfig() {
      if (this.categoryType === 'academic') return ACADEMIC_EXCELLENCE_OPTIONS;
      if (this.categoryType === 'skills_development') return SKILLS_DEVELOPMENT_OPTIONS;
      if (this.categoryType === 'career_opportunities') return CAREER_OPPORTUNITIES_OPTIONS;
      if (this.categoryType === 'career_preparation') return CAREER_PREPARATION_OPTIONS;
      if (this.categoryType === 'education') return EDUCATION_OPTIONS;
      return { title: 'Unknown', description: '', items: [] };
    },
    categoryTitle() {
      return this.categoryConfig.title;
    },
    categoryDescription() {
      return this.categoryConfig.description;
    },
    userSettings() {
      return {
        requiresPayment: this.getPaymentStatus,
        requiresInAppPayment: this.getInAppPaymentStatus,
        aiSkillsEnabled: this.getLoggedInUser?.linked_entity?.ai_skills_enabled || false,
      };
    },
    // Get dashboard config from API
    dashboardConfig() {
      return get(this.getLoggedInUser, 'linked_entity.std_dashboard_config.dashboard_config') || [];
    },
    // Categorize dashboard items from API config
    categorizedDashboardItems() {
      return categorizeDashboardItems(this.dashboardConfig);
    },
    filteredItems() {
      // For SCHOOL type, merge API config with hardcoded items for missing ones
      if (this.getLoggedInUserDashboard === DASHBOARD_TYPES.SCHOOL) {
        let apiItems = [];
        if (this.categoryType === 'academic') {
          apiItems = this.categorizedDashboardItems.academic || [];
        } else if (this.categoryType === 'skills_development') {
          apiItems = this.categorizedDashboardItems.skills_development || [];
        } else if (this.categoryType === 'career_opportunities') {
          apiItems = this.categorizedDashboardItems.career_opportunities || [];
        } else if (this.categoryType === 'career_preparation') {
          apiItems = this.categorizedDashboardItems.career_preparation || [];
        }
        // Merge API items with missing hardcoded items
        const merged = mergeWithStaticConfig(apiItems, this.categoryConfig, this.getLoggedInUserDashboard, this.userSettings);
        
        // If no items from merge (API data not loaded), return all static items
        if (merged.length === 0) {
          return getCategoryItems(this.categoryConfig, this.getLoggedInUserDashboard, this.userSettings);
        }
        
        return merged;
      }
      // For other dashboard types, use static config only
      return getCategoryItems(this.categoryConfig, this.getLoggedInUserDashboard, this.userSettings);
    },
  },
  methods: {
    ...mapActions('student', ['getStudentDashboardStatus', 'getStudTranscript']),
    ...mapActions('fileDownload', ['downloadFile']),
    ...mapActions('school', ['getSchoolPaymentStatus']),
    get,

    goBack() {
      this.$router.push({ name: 'student-dashboard' });
    },

    async handleActionClick(item) {
      if (item.id === 'program-certificate') {
        await this.handleTranscriptDownload();
      }
    },

    async handleTranscriptDownload() {
      try {
        const user = this.getLoggedInUser;
        const resp = await this.getStudTranscript({ id: user.id });
        const h = this.$createElement;
        const url = resp.data.data.url;

        if (url) {
          await this.downloadFile({ fileUrl: resp.data.data.url });
          this.makeToast({
            variant: 'success',
            msg: h('span', [h('i', { class: 'fas fa-download mr-2' }), 'File Downloaded']),
            toaster: 'b-toaster-bottom-left',
          });
        } else {
          this.makeToast({
            variant: 'danger',
            msg: h('span', ['Transcript Not Found']),
            toaster: 'b-toaster-top-right',
          });
        }
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    async fetchDashboardStatus() {
      try {
        this.setupStatuses = (await this.getStudentDashboardStatus()).data;
        this.isDataLoaded = true;
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        this.isDataLoaded = true;
      }
    },
  },
  async mounted() {
    if (this.getLoggedInUserDashboard === DASHBOARD_TYPES.SCHOOL) {
      try {
        await this.getSchoolPaymentStatus();
      } catch (_err) {
        // Keep page usable even if payment status call fails
      } finally {
        this.isPaymentStatusLoaded = true;
      }

      await this.fetchDashboardStatus();
      return;
    }

    this.isPaymentStatusLoaded = true;
  },
};
</script>

<style lang="scss" scoped>
.page-section {
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
}

.category-header {
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.back-button {
  background: transparent;
  border: none;
  color: #5e72e4;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(94, 114, 228, 0.1);
  }

  ::v-deep .md-icon {
    font-size: 18px !important;
    line-height: 1;
  }
}

.category-title {
  margin-bottom: 0 !important;
  margin-top: 10px;
  margin-bottom: 2px !important;
  line-height: 1.25;
}

.category-desc {
  margin-top: 2px;
  margin-bottom: 20px !important;
}
</style>
